import { useState, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/shared/components/ui/command';

export interface SlashCommandItem {
  key: string;
  label: string;
  description?: string;
  icon: ReactNode;
  category: 'text' | 'lists' | 'blocks' | 'media';
  command: () => void;
}

interface SlashCommandMenuProps {
  items: SlashCommandItem[];
  query: string;
  onSelect: (item: SlashCommandItem) => void;
  onClose: () => void;
}

const CATEGORY_ORDER = ['text', 'lists', 'blocks', 'media'] as const;

/**
 * Slash command palette styled on top of shadcn's Command (cmdk).
 * Keyboard nav is driven by TipTap's Suggestion plugin (the editor keeps focus),
 * so we keep our own document key handler and reflect the active item through the
 * controlled `value` prop rather than letting cmdk own the keyboard.
 */
export function SlashCommandMenu({ items, query, onSelect, onClose }: SlashCommandMenuProps) {
  const { t } = useTranslation('richText');
  const [activeIndex, setActiveIndex] = useState(0);
  const [prevQuery, setPrevQuery] = useState(query);

  // Reset highlight when the query changes — update-during-render pattern (React docs).
  if (prevQuery !== query) {
    setPrevQuery(query);
    setActiveIndex(0);
  }

  const filtered = items.filter(
    (item) =>
      item.label.toLowerCase().includes(query.toLowerCase()) ||
      item.key.toLowerCase().includes(query.toLowerCase()),
  );

  const categories = CATEGORY_ORDER.filter((cat) => filtered.some((item) => item.category === cat));
  const flatFiltered = categories.flatMap((cat) =>
    filtered.filter((item) => item.category === cat),
  );
  const activeKey = flatFiltered[activeIndex]?.key ?? '';

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, flatFiltered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = flatFiltered[activeIndex];
        if (item) onSelect(item);
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, flatFiltered, onSelect, onClose]);

  return (
    <Command
      shouldFilter={false}
      value={activeKey}
      onValueChange={(key) => {
        const idx = flatFiltered.findIndex((item) => item.key === key);
        if (idx >= 0) setActiveIndex(idx);
      }}
      data-testid="rich-text-slash-menu"
      className="w-72 rounded-xl border border-border/60 bg-popover shadow-xl ring-1 ring-border/30"
    >
      <CommandList className="max-h-72 p-1">
        <CommandEmpty>{t('slashMenu.noResults')}</CommandEmpty>
        {categories.map((cat) => {
          const catItems = filtered.filter((item) => item.category === cat);
          if (catItems.length === 0) return null;

          return (
            <CommandGroup key={cat} heading={t(`slashMenu.categories.${cat}`)}>
              {catItems.map((item) => (
                <CommandItem
                  key={item.key}
                  value={item.key}
                  onSelect={() => onSelect(item)}
                  data-testid={`rich-text-slash-item-${item.key}`}
                  className="group gap-3 rounded-lg px-2 py-2"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent bg-muted text-muted-foreground transition-colors group-data-[selected=true]:border-primary/15 group-data-[selected=true]:bg-primary/10 group-data-[selected=true]:text-primary">
                    {item.icon}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{item.label}</div>
                    {item.description && (
                      <div className="truncate text-xs text-muted-foreground">
                        {item.description}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </Command>
  );
}
